"use client";

import { useEffect, useMemo, useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { ArrowLeft, CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import { CommandEmpty, CommandGroup, CommandList } from "@/shared/ui/command";
import { getChildren, getScope, getScopeChain } from "../registry";
import { rankScopesForPage } from "../search/rank";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ResultRow } from "../components/ResultRow";
import { PaletteFooter } from "../components/PaletteFooter";
import type { PaletteCtx, Result, ScopeNode } from "../types";

export function ScopePage({ ctx, scopeId }: { ctx: PaletteCtx; scopeId: string }) {
  const scope = getScope(scopeId);
  const [input, setInput] = useState("");
  const [dynamic, setDynamic] = useState<ScopeNode[]>([]);
  const [loading, setLoading] = useState(() => !!scope?.resolveChildren);

  // A different scope (or context) starts over. Applied during render, so the
  // previous scope's children never paint under the new title.
  const [resolvedFor, setResolvedFor] = useState({ scope, ctx });
  if (resolvedFor.scope !== scope || resolvedFor.ctx !== ctx) {
    setResolvedFor({ scope, ctx });
    setDynamic([]);
    if (scope?.resolveChildren) setLoading(true);
  }

  useEffect(() => {
    if (!scope?.resolveChildren) return;
    const ac = new AbortController();
    scope
      .resolveChildren(ctx)
      .then((nodes) => {
        if (!ac.signal.aborted) setDynamic(nodes);
      })
      .catch(() => {})
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [scope, ctx]);

  const allChildren = useMemo(() => {
    if (!scope) return [];
    return [...getChildren(scope.id), ...dynamic];
  }, [scope, dynamic]);

  const results: Result[] = useMemo(() => {
    if (input.trim()) return rankScopesForPage(allChildren, input.trim(), scope?.id);
    return allChildren.map<Result>((c) => ({
      id: `scope:${c.id}`,
      title: c.title,
      subtitle: c.subtitle,
      icon: c.icon,
      scopeChain: [],
      score: 0,
      onSelect: (cx: PaletteCtx) => {
        // Priority: explicit onSelect > href navigation > drill.
        // Dynamic leaves (Spaces, etc.) use onSelect to "do the action
        // and close" rather than drilling into a registry node that
        // doesn't exist (which would break breadcrumbs).
        if (c.onSelect) {
          c.onSelect(cx);
          return;
        }
        if (c.href) {
          cx.router.push(c.href);
          cx.close();
          return;
        }
        cx.push({ kind: "scope", scopeId: c.id });
      },
    }));
  }, [allChildren, input, scope?.id]);

  if (!scope) return null;
  const chain = getScopeChain(scope.id);

  return (
    <>
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3">
        <button
          type="button"
          onClick={ctx.pop}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground outline-none transition-[color,background-color] duration-(--dur-fast) hover:bg-accent hover:text-foreground focus-visible:ring-(length:--ring-w) focus-visible:ring-ring"
          aria-label="Back"
        >
          <ArrowLeft className="size-4" />
        </button>
        <Breadcrumbs
          chain={chain}
          size="md"
          onJump={(id) => {
            ctx.reset();
            if (id !== chain[0].id) ctx.push({ kind: "scope", scopeId: id });
          }}
        />
      </div>

      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-5">
        <MagnifyingGlass className="size-5 text-muted-foreground/70 shrink-0" />
        <CommandPrimitive.Input
          autoFocus
          value={input}
          onValueChange={setInput}
          data-palette-input="true"
          placeholder={`Search in ${scope.title}…`}
          className="flex-1 h-full bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/60"
        />
        {loading && <CircleNotch className="size-4 animate-spin text-muted-foreground/70" />}
      </div>

      <CommandList
        className="
          max-h-none flex-1 overflow-y-auto p-2
          [&_[cmdk-group-heading]]:px-3
          [&_[cmdk-group-heading]]:pb-1
          [&_[cmdk-group-heading]]:pt-3
          [&_[cmdk-group-heading]]:text-caption
          [&_[cmdk-group-heading]]:font-semibold
          [&_[cmdk-group-heading]]:uppercase
          [&_[cmdk-group-heading]]:tracking-wider
          [&_[cmdk-group-heading]]:text-muted-foreground/70
        "
      >
        <CommandEmpty className="py-16 text-center text-sm text-muted-foreground">
          {loading ? "Loading…" : "Nothing in this scope."}
        </CommandEmpty>
        <CommandGroup heading={scope.title}>
          {results.map((r) => (
            <ResultRow key={r.id} result={r} ctx={ctx} showBreadcrumbs={false} />
          ))}
        </CommandGroup>
      </CommandList>

      <PaletteFooter />
    </>
  );
}
